import Permissions from '../Permissions/Permissions'
import { is } from '../is/is'

type TKind = 'file' | 'string'

const navi = window.navigator

/**
 * Saját wrapper a navigator.clipboard fölött.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Clipboard}
 */
const Clipboard = {
  /**
   * Vágólap eseményből kép vagy szöveg visszaadása.
   * @param e    - A vágólap esemény.
   * @param kind - Milyen típusú a clipboard data.
   */
  getContentFromEvent: (e: ClipboardEvent, kind: TKind): Promise<string | null> => {
    return new Promise((resolve) => {
      const [ x ] = is.null(e.clipboardData)
        ? []
        : Array.from(e.clipboardData.items) ?? []

      if (x.kind !== kind) {
        resolve(null)

        return
      }

      const blob = x.getAsFile()

      if (!blob) {
        resolve(null)

        return
      }

      // eslint-disable-next-line no-restricted-globals -- FileReader.
      const reader = new FileReader()

      reader.addEventListener('load', (event) => {
        const { result } = event?.target ?? { result: null }

        if (is.string(result)) {
          resolve(result)
        }
        else {
          resolve(null)
        }
      })

      reader.readAsDataURL(blob)
    })
  },

  /**
   * Szöveges adat beállítása a vágólapra.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText}
   * @param content - A vágólapra helyezendő szöveg.
   */
  setContent: async (content: string): Promise<boolean> => {
    try {
      await navi.clipboard.writeText(content)

      return true
    }
    catch (error) {
      // Maga az error itt nem fog információt hordozni.
      // Csak akkor fog error-ral elszállni, ha nincs meg a megfelelő jogosultság.
      // eslint-disable-next-line no-console -- Ide kell a log.
      console.log('[Clipboard] setContent', error)
    }

    return false
  },

  /**
   * Visszaadja, hogy lehet-e olvasni a vágólapot.
   * Egyes böngészők alapból nem támogatják (FF), más böngészők pedig engedélyt kérnek hozzá.
   */
  canRead: async (): Promise<boolean> => {
    const isSupported = is.property(navi.clipboard, 'readText')

    if (!isSupported) {
      return false
    }

    const isGranted = await Permissions.isGranted('clipboard-read')

    return isGranted
  },

  /**
   * Szöveges adat kiolvasása vágólapról.
   * @see {@link https://caniuse.com/mdn-api_clipboard_readtext}
   * @deprecated Ez NEM cross-browser kompatibilis, az FF nem támogatja. Csak akkor használd, ha tudod mit csinálsz!
   */
  getContent: async (): Promise<string | null> => {
    try {
      const content = await navi.clipboard.readText()

      return content
    }
    catch (error) {
      // eslint-disable-next-line no-console -- Ide kell a log.
      console.log('[Clipboard] getContent', error)
    }

    return null
  }
}

export default Clipboard
